import React, { useEffect, useMemo, useState } from "react";
import { resolvePlannedBillingMilestoneAmount } from "@/finance/billingMilestoneAmounts";
import { parseKpiFiltersFromUrl } from "@/finance/financeIncomeKpiNavigation";
import {
  toggleFinanceIncomeSort,
  type FinanceIncomeSort,
  type FinanceIncomeSortKey,
} from "@shared/finance/financeIncomeSorting";
import {
  INVOICE_PAID_PENDING_ECONOMIC_REVIEW_LABEL,
  INVOICE_REVIEW_REQUIRED_LABEL,
  isInvoicePaidPendingEconomicReview,
  isInvoiceCollectionComplete as isLedgerInvoiceCollectionComplete,
  billingMilestoneDisplayStatus,
} from "@/finance/billingCollectionPresentation";
import { getBillingInvoiceDueDate, isBillingInvoiceOverdue } from "@shared/billing/billing-due-date";
import { calculateInvoiceVatAmount, type BillingVatRate } from "@shared/billing/invoice-vat";
import { BillingVatRateSelect } from "../billing/BillingVatRateSelect";
import { formatNetBankAmount } from "./accountingDashboardUtils";

import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import {
  FileText,
  Clock,
  TrendingUp,
  AlertCircle,
  CheckCircle2,
  Search,
  X,
  ChevronLeft,
  ChevronRight,
  Eye,
  DollarSign,
  ChevronDown,
  ChevronUp,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
} from "lucide-react";
import { useAuth } from "@//_core/hooks/useAuth";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  FinanceMetricCard,
  FinanceMetricGrid,
  FinanceSecondaryMetrics,
} from "@/components/finance/ui";

// Helper para normalizar fechas
const normalizeDate = (value: string | Date | null | undefined): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === "string") {
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
};

const parseSettlementMoney = (value: string): number | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const normalized = /^[0-9]+(?:\.[0-9]{1,2})?$/.test(trimmed)
    ? trimmed
    : /^[0-9]+(?:\.[0-9]{3})*,[0-9]{1,2}$/.test(trimmed)
      ? trimmed.replace(/\./g, "").replace(",", ".")
      : "";
  if (!normalized) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

// Tipos locales para AccountingDashboard.tsx
type BillingMilestone = {
  id: number;
  status: string;
  type: "fixed_amount" | "percentage" | "monthly_certification";
  typeLabel: string;
  label: string | null;
  description: string | null;
  amount: number;
  legacyEconomicInconsistency?: boolean;
  milestoneId?: number;
  milestoneConcept?: string | null;
  expectedInvoiceDate?: Date | null;
  dueDate: Date | null;
  paymentDueDate?: Date | null;
  daysOverdue: number;
  invoiceNumber: string | null;
  invoiceDocumentUrl?: string | null;
  invoiceDate: Date | null;
  invoiceAmount?: string | null;
  invoiceTaxableBase?: string | null;
  invoiceTaxAmount?: string | null;
  invoiceWithholdingAmount?: string | null;
  invoiceFiscalVerified?: boolean;
  invoiceEconomicReviewRequired?: boolean;
  invoiceEconomicReviewMessage?: string | null;
  paymentDate?: Date | null;
  paymentAmount?: number | null;
  collectedAmount?: string | null;
  balanceAmount?: string | null;
  collectionEvents?: Array<{
    id?: number | string;
    eventType?: string;
    source?: string;
    amount?: string | number | null;
    occurredAt?: Date | string | null;
  }>;
  paymentMethod?: string | null;
  effectivePaymentMethod?: string | null;
  bankCommissionAmount?: string | null;
  bankInterestAmount?: string | null;
  financialCostAmount?: string | null;
  netReceivedAmount?: string | null;
  paymentTerms: string | null;
  paymentTermBasis?: "invoice_date" | "end_of_month" | "client_acceptance" | "other" | null;
  collectionStatus?: string;
  historicalCollectionConflict?: boolean;
  isOverdue: boolean;
  paymentDelayDays?: number | null;
  clientName: string;
  clientId: string;
  contractCode?: string | null;
  contractTitle?: string | null;
  installationCode?: string | null;
  installationId: number | null;
  contractId?: number | null;
  certificationId?: number | null;
  installationName: string | null;
  workOrderType?: "installation" | "breakdown" | "maintenance";
  links?: { installationPath: string | null; billingPath: string | null };
  accountingNotifiedAt: Date | null;
  noticeSummary?: BillingNoticeSummary;
  createdAt: Date;
  updatedAt: Date;
};

type BillingNoticeSummary = {
  noticeCount: number;
  latestNotice: BillingNoticeCompact | null;
  hasRetried: boolean;
  hasFailed: boolean;
  hasSuperseded: boolean;
};

type BillingNoticeCompact = {
  id: number;
  status: string;
  recipientEmail: string;
  recipientName: string | null;
  attempt: number;
  emailLogId: number | null;
  createdAt: Date | string;
  sentAt: Date | string | null;
  failedAt: Date | string | null;
  supersededAt: Date | string | null;
  cancelledAt: Date | string | null;
};

function hasRecordedCollectionEvidence(
  milestone: Pick<BillingMilestone, "collectedAmount" | "paymentAmount" | "collectionEvents">,
): boolean {
  const hasValidMovement = (milestone.collectionEvents ?? []).some((event) => {
    if (event.eventType !== "collection" && event.eventType !== "reversal") return false;
    const amount = event.amount == null ? null : parseSettlementMoney(String(event.amount));
    return amount !== null && amount > 0;
  });
  if (hasValidMovement) return true;

  return [milestone.collectedAmount, milestone.paymentAmount].some((value) => {
    if (value == null) return false;
    const amount = parseSettlementMoney(String(value));
    return amount !== null && amount > 0;
  });
}

type KpiBucket = {
  count: number;
  amountCents: number;
};

type KpiSummary = {
  planned?: KpiBucket;
  pending_invoice_issue?: KpiBucket;
  invoice_registered_followup?: KpiBucket;
  collection_followup?: KpiBucket;
  overdue_uncollected?: KpiBucket;
  collected?: KpiBucket;
  cancelled?: KpiBucket;
  amount_unverifiable?: KpiBucket;
};

type FinanceAmountTotal = {
  count: number;
  amountCents: number;
  amountUnknownCount: number;
  taxAmountCents?: number;
  withholdingAmountCents?: number;
  ledgerAmountCents?: number;
  legacyPaymentSnapshotAmountCents?: number;
  ledgerBalanceConflictCount?: number;
  bankUnverifiedCount?: number;
};

const STATUS_OPTIONS = [
  { value: "pending", label: "Planificado / no facturable aún" },
  { value: "ready_to_invoice", label: "Listo para facturar" },
  { value: "accounting_notified", label: "Avisado a contabilidad" },
  { value: "invoiced", label: "Factura emitida" },
  { value: "sent_to_client", label: "Enviado al cliente" },
  { value: "pending_collection", label: "Pendiente de cobro" },
  { value: "collected", label: "Cobrado" },
  { value: "cancelled", label: "Cancelado" },
] as const;

type AccountingDashboardMode = "summary" | "income";

type AccountingDashboardProps = {
  mode?: AccountingDashboardMode;
  initialStatusFilters?: string[];
  initialOverdueFilter?: "yes" | "no" | "all";
  titleOverride?: string;
  descriptionOverride?: string;
};

const ACCOUNTING_DASHBOARD_COPY: Record<AccountingDashboardMode, { title: string; description: string }> = {
  summary: {
    title: "Resumen financiero",
    description: "Indicadores operativos de facturación, cobros y vencimientos con datos reales actuales.",
  },
  income: {
    title: "Facturación e ingresos",
    description: "Hitos de facturación, facturas emitidas y seguimiento de cobro.",
  },
};

export function AccountingDashboard({
  mode = "summary",
  initialStatusFilters = [],
  initialOverdueFilter = "all",
  titleOverride,
  descriptionOverride,
}: AccountingDashboardProps) {
  const { user } = useAuth();
  const isManager = ["admin", "project_manager", "admin_manager"].includes(
    user?.role || ""
  );
  const isFinanceManager = ["admin", "admin_manager"].includes(user?.role || "");
  const pageCopy = {
    title: titleOverride || ACCOUNTING_DASHBOARD_COPY[mode].title,
    description: descriptionOverride || ACCOUNTING_DASHBOARD_COPY[mode].description,
  };
  const initialQueryFilters = useMemo(() => {
    if (typeof window === "undefined") return { statuses: initialStatusFilters, overdue: initialOverdueFilter };
    const { statuses, overdueFilter } = parseKpiFiltersFromUrl(window.location.search, {
      statuses: initialStatusFilters,
      overdueFilter: initialOverdueFilter,
    });
    return { statuses, overdue: overdueFilter };
  }, [initialOverdueFilter, initialStatusFilters]);

  // Filter state
  const [statusFilters, setStatusFilters] = useState<string[]>(initialQueryFilters.statuses);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [clientIdFilter, setClientIdFilter] = useState<string>("");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [overdueFilter, setOverdueFilter] = useState<string>(initialQueryFilters.overdue);
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const [offset, setOffset] = useState<number>(0);
  const [limit, setLimit] = useState<number>(50);
  const [expandedMilestones, setExpandedMilestones] = useState<Set<number>>(() => new Set());
  const [incomeSort, setIncomeSort] = useState<FinanceIncomeSort | null>(null);
  const [settlementMilestone, setSettlementMilestone] = useState<BillingMilestone | null>(null);
  const [settlementDate, setSettlementDate] = useState("");
  const [settlementGross, setSettlementGross] = useState("");
  const [settlementCommission, setSettlementCommission] = useState("0.00");
  const [settlementInterest, setSettlementInterest] = useState("0.00");
  const [settlementError, setSettlementError] = useState<string | null>(null);
  const [settlementMethod, setSettlementMethod] = useState("transfer");
  const [settlementReference, setSettlementReference] = useState("");
  const [settlementIdempotencyKey, setSettlementIdempotencyKey] = useState("");

  const [invoicingMilestone, setInvoicingMilestone] = useState<BillingMilestone | null>(null);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceDate, setInvoiceDate] = useState("");
  const [invoiceTaxableBase, setInvoiceTaxableBase] = useState("");
  const [invoiceTaxAmount, setInvoiceTaxAmount] = useState("");
  const [invoiceVatRate, setInvoiceVatRate] = useState<BillingVatRate>("manual");
  const [invoiceWithholdingAmount, setInvoiceWithholdingAmount] = useState("");
  const handleInvoiceBaseChange = (value: string) => {
    setInvoiceTaxableBase(value);
    if (invoiceVatRate !== "manual") {
      const calculated = calculateInvoiceVatAmount(value, invoiceVatRate);
      if (calculated !== null) setInvoiceTaxAmount(calculated);
    }
  };
  const handleInvoiceVatRateChange = (value: string) => {
    const rate = value as BillingVatRate;
    setInvoiceVatRate(rate);
    const calculated = calculateInvoiceVatAmount(invoiceTaxableBase, rate);
    if (calculated !== null) setInvoiceTaxAmount(calculated);
  };
  const [invoiceError, setInvoiceError] = useState<string | null>(null);

  useEffect(() => {
    setOffset(0);
  }, [searchQuery, clientIdFilter, typeFilter, overdueFilter, dateFrom, dateTo]);

  const selectedStatusFilter =
    statusFilters.length === 0 || statusFilters.length === STATUS_OPTIONS.length
      ? undefined
      : statusFilters;

  // Query milestones for accounting dashboard
  const milestonesQuery = trpc.billingMilestones.accountingDashboard.useQuery({
    statusFilters: selectedStatusFilter,
    search: searchQuery || undefined,
    clientId: clientIdFilter || undefined,
    typeFilter: (typeFilter !== "all" ? typeFilter : undefined) as "fixed_amount" | "percentage" | "monthly_certification" | undefined,
    overdueFilter: (overdueFilter !== "all" ? overdueFilter : undefined) as "yes" | "no" | "all" | undefined,
    dateFrom: dateFrom ? new Date(dateFrom) : undefined,
    dateTo: dateTo ? new Date(dateTo) : undefined,
    offset,
    limit,
    sortKey: mode === "income" ? incomeSort?.key : undefined,
    sortDirection: mode === "income" ? incomeSort?.direction : undefined,
  });

  const formatMilestoneAmount = (milestone: BillingMilestone) =>
    milestone.legacyEconomicInconsistency
      ? "Base histórica no disponible"
      : milestone.amount.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });

  const formatEconomicReviewAmount = (amount: number) =>
    new Intl.NumberFormat("es-ES", {
      style: "currency",
      currency: "EUR",
      useGrouping: "always",
    }).format(amount);

  const renderInvoiceEconomicReviewAmounts = (milestone: BillingMilestone) =>
    isInvoicePaidPendingEconomicReview(milestone) ? (
      <div className="space-y-0.5 text-xs font-normal text-gray-600" data-testid="invoice-economic-review-amounts">
        <div>Planificado: {formatMilestoneAmount(milestone)}</div>
        {milestone.invoiceAmount != null ? <div>Factura exigible: {formatEconomicReviewAmount(Number(milestone.invoiceAmount))}</div> : null}
        <div>Cobrado: {formatEconomicReviewAmount(Number(milestone.collectedAmount ?? 0))}</div>
        {milestone.balanceAmount != null ? <div>Saldo factura: {formatEconomicReviewAmount(Number(milestone.balanceAmount))}</div> : null}
      </div>
    ) : null;

  const renderRecordedCollectionAmount = (milestone: BillingMilestone) => {
    if (milestone.status !== "collected" || isInvoicePaidPendingEconomicReview(milestone)) return null;
    const rawAmount = milestone.collectedAmount ?? (milestone.paymentAmount == null ? null : String(milestone.paymentAmount));
    const recordedAmount = rawAmount == null ? null : parseSettlementMoney(String(rawAmount));
    return (
      <div className="text-xs font-normal text-gray-600" data-testid="historical-collected-amount">
        Cobro registrado: {recordedAmount !== null && recordedAmount > 0 ? formatEuroAmount(recordedAmount) : "importe no disponible"}
      </div>
    );
  };

  const canRecordCollection = (milestone: BillingMilestone) => {
    const balance = milestone.balanceAmount == null ? null : parseSettlementMoney(milestone.balanceAmount);
    return isFinanceManager
      && ["invoiced", "sent_to_client", "pending_collection"].includes(milestone.status)
      && Boolean(milestone.invoiceNumber?.trim())
      && normalizeDate(milestone.invoiceDate) !== null
      && milestone.invoiceFiscalVerified === true
      && balance !== null
      && balance > 0;
  };

  const toggleStatusFilter = (status: string) => {
    setOffset(0);
    setStatusFilters((current) =>
      current.includes(status)
        ? current.filter((item) => item !== status)
        : [...current, status]
    );
  };

  const statusFilterLabel = useMemo(() => {
    if (statusFilters.length === 0 || statusFilters.length === STATUS_OPTIONS.length) {
      return "Todos los estados";
    }
    if (statusFilters.length === 1) {
      return STATUS_OPTIONS.find((option) => option.value === statusFilters[0])?.label || "1 estado seleccionado";
    }
    return `${statusFilters.length} estados seleccionados`;
  }, [statusFilters]);

  // Mutations for status actions
  const markInvoicedMutation = trpc.billingMilestones.markInvoiced.useMutation();
  const markSentToClientMutation = trpc.billingMilestones.markSentToClient.useMutation();
  const markPendingCollectionMutation = trpc.billingMilestones.markPendingCollection.useMutation();
  const recordCollectionMutation = trpc.billingMilestones.recordCollection.useMutation();

  const kpiSummary = (milestonesQuery.data?.kpiSummary || {}) as KpiSummary;
  const financialTotals = milestonesQuery.data?.financialTotals as {
    invoiced?: FinanceAmountTotal;
    invoicePayable?: FinanceAmountTotal;
    collected?: FinanceAmountTotal;
  } | undefined;
  const unknownInvoiceAmountCount = financialTotals?.invoiced?.amountUnknownCount ?? 0;
  const unknownCollectedAmountCount = financialTotals?.collected?.amountUnknownCount ?? 0;
  const collectedLedgerConflictCount = financialTotals?.collected?.ledgerBalanceConflictCount ?? 0;
  const collectedLedgerAmountCents = financialTotals?.collected?.ledgerAmountCents ?? 0;
  const collectedLegacyPaymentSnapshotAmountCents = financialTotals?.collected?.legacyPaymentSnapshotAmountCents ?? 0;
  const collectedBankUnverifiedCount = financialTotals?.collected?.bankUnverifiedCount ?? 0;
  const invoiceTaxAmountCents = financialTotals?.invoicePayable?.taxAmountCents ?? 0;
  const invoiceWithholdingAmountCents = financialTotals?.invoicePayable?.withholdingAmountCents ?? 0;

  // Filter milestones by search query
  const filteredMilestones = useMemo(() => {
    let milestones = milestonesQuery.data?.milestones || [];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      milestones = milestones.filter((m: BillingMilestone) => {
        const searchableValues = [
          m.clientName,
          m.clientId,
          m.label,
          m.installationName,
          m.installationCode,
          m.invoiceNumber,
          m.contractCode,
          m.contractTitle,
        ];
        return searchableValues.some((value) => value?.toLowerCase().includes(query));
      });
    }

    return milestones;
  }, [milestonesQuery.data, searchQuery]);

  const displayedMilestones = filteredMilestones;

  const requestIncomeSort = (key: FinanceIncomeSortKey) => {
    setIncomeSort(current => toggleFinanceIncomeSort(current, key));
  };

  const renderIncomeSortHeader = (
    key: FinanceIncomeSortKey,
    label: string,
    className = "",
  ) => {
    const direction = incomeSort?.key === key ? incomeSort.direction : null;
    const Icon = !direction
      ? ArrowUpDown
      : direction === "desc"
        ? ArrowDown
        : ArrowUp;

    return (
      <TableHead
        className={className}
        aria-sort={
          direction
            ? direction === "desc"
              ? "descending"
              : "ascending"
            : "none"
        }
      >
        <button
          type="button"
          onClick={() => requestIncomeSort(key)}
          aria-label={`Ordenar ${label} ${direction === "desc" ? "ascendente" : "descendente"}`}
          className="inline-flex items-center gap-1 hover:text-foreground focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <span>{label}</span>
          <Icon aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
        </button>
      </TableHead>
    );
  };

  // Calculate pagination
  const totalMilestones = milestonesQuery.data?.pagination?.totalCount || 0;
  const canPrevious = offset > 0;
  const canNext = offset + limit < totalMilestones;

  // Helper para calcular vencimiento real (invoiceDate + paymentTerms)
  const calculateRealDueDate = (milestone: BillingMilestone): Date | null => {
    return getBillingInvoiceDueDate(milestone);
  };

  const formatNoticeDateTime = (value: Date | string | null | undefined) => {
    if (!value) return "-";
    const date = value instanceof Date ? value : new Date(value);
    return isNaN(date.getTime()) ? "-" : date.toLocaleString("es-ES");
  };

  const getEffectiveNoticeStatus = (notice: BillingNoticeCompact | null | undefined) => {
    if (!notice) return null;
    if (notice.status === "pending" && (notice.sentAt || notice.emailLogId != null)) return "sent";
    return notice.status;
  };

  const getNoticeStatusLabel = (status?: string | null) => {
    const labels: Record<string, string> = {
      sent: "enviado",
      failed: "fallido",
      superseded: "invalidado",
      pending: "pendiente",
      cancelled: "cancelado",
    };
    return status ? labels[status] || status : "-";
  };

  const getNoticeMainDate = (notice: BillingNoticeCompact | null | undefined) =>
    notice ? notice.sentAt || notice.failedAt || notice.supersededAt || notice.cancelledAt || notice.createdAt : null;

  const renderNoticeSummary = (noticeSummary?: BillingNoticeSummary, compact = false) => {
    if (!noticeSummary || noticeSummary.noticeCount === 0 || !noticeSummary.latestNotice) {
      return <span className="text-gray-400">Sin avisos</span>;
    }

    const latest = noticeSummary.latestNotice;
    const latestStatus = getEffectiveNoticeStatus(latest);
    return (
      <div className={compact ? "space-y-1 text-xs" : "space-y-2 text-sm"}>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline" className="bg-gray-50">
            Último aviso: {getNoticeStatusLabel(latestStatus)}
          </Badge>
          <span className="text-gray-600">{formatNoticeDateTime(getNoticeMainDate(latest))}</span>
        </div>
        <div className="flex flex-wrap gap-2 text-xs text-gray-600">
          <span>{noticeSummary.noticeCount} aviso{noticeSummary.noticeCount === 1 ? "" : "s"}</span>
          <span>Intento {latest.attempt}</span>
          {noticeSummary.hasRetried ? <span className="font-medium text-blue-700">reaviso</span> : null}
          {noticeSummary.hasFailed ? <span className="font-medium text-red-700">fallido</span> : null}
          {noticeSummary.hasSuperseded ? <span className="font-medium text-slate-700">invalidado</span> : null}
        </div>
        <div className="break-words [overflow-wrap:anywhere] text-xs text-gray-500">
          {latest.recipientName || latest.recipientEmail}
          {latest.emailLogId != null ? ` · emailLogId ${latest.emailLogId}` : ""}
        </div>
      </div>
    );
  };

  const getCollectedPaymentLabel = (milestone: BillingMilestone) => {
    const netAmount = milestone.netReceivedAmount;
    const hasNetAmount = netAmount != null
      && (hasRecordedCollectionEvidence(milestone)
        || (parseSettlementMoney(netAmount) ?? 0) > 0);
    const netSuffix = hasNetAmount && netAmount != null
      ? ` · Neto banco: ${formatNetBankAmount(netAmount)}`
      : "";
    if (!milestone.paymentDate) return `Cobrado · sin fecha registrada${netSuffix}`;

    const parsedPaymentDate = normalizeDate(milestone.paymentDate);
    if (!parsedPaymentDate) return `Cobrado · sin fecha registrada${netSuffix}`;

    const paymentDate = parsedPaymentDate.toLocaleDateString("es-ES");
    if (milestone.paymentDelayDays != null && milestone.paymentDelayDays > 0) {
      return `Cobrado: ${paymentDate} · ${milestone.paymentDelayDays} días de retraso${netSuffix}`;
    }
    if (milestone.paymentDelayDays != null && milestone.paymentDelayDays <= 0) {
      return `Cobrado: ${paymentDate} · en plazo${netSuffix}`;
    }
    return `Cobrado: ${paymentDate}${netSuffix}`;
  };

  // Calculate total amounts by status category — source: kpiSummary (full filtered universe).
  // Never use milestonesQuery.data.milestones (current page) as KPI source.
  const totalsByCategory = useMemo(() => {
    const asEuros = (bucket?: KpiBucket) => (bucket?.amountCents || 0) / 100;
    const pendingToInvoice = asEuros(kpiSummary.pending_invoice_issue);
    const pendingToInvoiceCount = kpiSummary.pending_invoice_issue?.count || 0;
    const invoicedPendingCollection = asEuros(kpiSummary.invoice_registered_followup);
    const invoicedPendingCollectionCount = kpiSummary.invoice_registered_followup?.count || 0;
    const pendingCollection = asEuros(kpiSummary.collection_followup);
    const pendingCollectionCount = kpiSummary.collection_followup?.count || 0;
    const overdueTotal = asEuros(kpiSummary.overdue_uncollected);
    const overdueCount = kpiSummary.overdue_uncollected?.count || 0;
    const collected = asEuros(kpiSummary.collected);
    const collectedCount = kpiSummary.collected?.count || 0;
    const pending = asEuros(kpiSummary.planned);
    const pendingCount = kpiSummary.planned?.count || 0;
    const cancelled = asEuros(kpiSummary.cancelled);
    const cancelledCount = kpiSummary.cancelled?.count || 0;

    return { pendingToInvoice, pendingToInvoiceCount, invoicedPendingCollection, invoicedPendingCollectionCount, pendingCollection, pendingCollectionCount, overdue: overdueTotal, overdueCount, collected, collectedCount, pending, pendingCount, cancelled, cancelledCount };
  }, [kpiSummary]);

  const formatEuroAmount = (amount: number) =>
    amount.toLocaleString("es-ES", { style: "currency", currency: "EUR" });

  const renderIncomeCollectionMovements = (milestone: BillingMilestone) => {
    const events = (milestone.collectionEvents ?? []).filter((event) =>
      event.eventType === "collection" || event.eventType === "reversal",
    );
    const displayEvents = events.length > 0
      ? events
      : (() => {
          const amountValue = milestone.collectedAmount ?? milestone.paymentAmount;
          const amount = amountValue == null ? null : parseSettlementMoney(String(amountValue));
          if (!milestone.paymentDate && !(amount != null && amount > 0)) return [];
          return [{
            id: `legacy-payment-${milestone.id}`,
            eventType: "collection",
            source: "legacy_snapshot",
            amount: amountValue,
            occurredAt: milestone.paymentDate,
          }];
        })();

    if (displayEvents.length === 0) return null;

    return (
      <div className="space-y-0.5 pt-1 text-xs font-normal text-gray-500" data-testid="income-collection-movements">
        {displayEvents.map((event, index) => {
          const isReversal = event.eventType === "reversal";
          const isLegacySnapshot = event.source === "legacy_baseline" || event.source === "legacy_snapshot";
          const amount = event.amount == null ? null : parseSettlementMoney(String(event.amount));
          const movementAmount = amount == null
            ? "importe no disponible"
            : formatEuroAmount(isReversal ? -amount : amount);
          const eventDate = normalizeDate(event.occurredAt);
          const movementDate = eventDate
            ? eventDate.toLocaleDateString("es-ES")
            : "fecha no registrada";
          const movementLabel = isReversal ? "Reverso" : isLegacySnapshot ? "Cobro histórico" : "Cobro";

          return (
            <div key={String(event.id ?? index)} data-testid="income-collection-movement">
              {movementLabel}: {movementDate} · {movementAmount}
            </div>
          );
        })}
      </div>
    );
  };

  const formatStoredEuroAmount = (value: string | number) => {
    const parsed = parseSettlementMoney(String(value));
    return parsed === null ? `${value} €` : formatEconomicReviewAmount(parsed);
  };

  const formatFiscalBreakdownAmount = (value: string | null | undefined) => {
    if (value == null) return "—";
    const parsed = parseSettlementMoney(value);
    return parsed === null ? value : formatEconomicReviewAmount(parsed);
  };

  const primaryFinanceKpis = [
    {
      key: "pendingToInvoice",
      title: "Listo para facturar",
      amount: totalsByCategory.pendingToInvoice,
      count: totalsByCategory.pendingToInvoiceCount,
      description: "Preparados o ya avisados a Contabilidad",
      icon: FileText,
      variant: "blue" as const,
    },
    {
      key: "invoicedPendingCollection",
      title: "Emitidas · seguimiento",
      amount: totalsByCategory.invoicedPendingCollection,
      count: totalsByCategory.invoicedPendingCollectionCount,
      description: "Facturas emitidas o enviadas aún en seguimiento; no es el total facturado acumulado",
      icon: DollarSign,
      variant: "emerald" as const,
    },
    {
      key: "pendingCollection",
      title: "Pendiente de cobro",
      amount: totalsByCategory.pendingCollection,
      count: totalsByCategory.pendingCollectionCount,
      description: "Facturas en seguimiento hasta registrar cobro",
      icon: TrendingUp,
      variant: "amber" as const,
    },
    {
      key: "overdue",
      title: "Vencido sin cobrar",
      amount: totalsByCategory.overdue,
      count: totalsByCategory.overdueCount,
      description: "Vencimiento superado sin cobro registrado",
      icon: AlertCircle,
      variant: "rose" as const,
    },
  ];

  const secondaryFinanceKpis = [
    {
      key: "invoiced-total",
      label: "Base facturada (sin IVA)",
      value: formatEuroAmount((financialTotals?.invoiced?.amountCents ?? 0) / 100),
      count: financialTotals?.invoiced?.count ?? 0,
      badge: unknownInvoiceAmountCount > 0 ? `${unknownInvoiceAmountCount} sin importe verificado` : undefined,
      variant: "outline" as const,
    },
    {
      key: "invoice-payable-total",
      label: "Exigible facturado (IVA incluido)",
      value: formatEuroAmount((financialTotals?.invoicePayable?.amountCents ?? 0) / 100),
      count: financialTotals?.invoicePayable?.count ?? 0,
      badge: unknownInvoiceAmountCount > 0 ? `${unknownInvoiceAmountCount} sin importe verificado` : undefined,
      variant: "outline" as const,
    },
    {
      key: "collected",
      label: "Cobrado",
      value: formatEuroAmount((financialTotals?.collected?.amountCents ?? totalsByCategory.collected * 100) / 100),
      count: financialTotals?.collected?.count ?? totalsByCategory.collectedCount,
      badge: unknownCollectedAmountCount > 0 ? `${unknownCollectedAmountCount} sin importe acreditado` : undefined,
      variant: "emerald" as const,
    },
    {
      key: "pending",
      label: "Planificado",
      value: formatEuroAmount(totalsByCategory.pending),
      count: totalsByCategory.pendingCount,
      variant: "outline" as const,
    },
    {
      key: "cancelled",
      label: "Cancelado",
      value: formatEuroAmount(totalsByCategory.cancelled),
      count: totalsByCategory.cancelledCount,
      variant: "rose" as const,
    },
  ];

  const clearAllFilters = () => {
    setOffset(0);
    setStatusFilters([]);
    setSearchQuery("");
    setClientIdFilter("");
    setTypeFilter("all");
    setOverdueFilter("all");
    setDateFrom("");
    setDateTo("");
    if (typeof window !== "undefined" && window.location.search) {
      window.history.replaceState(null, "", window.location.pathname);
    }
  };

  // Handle navigation
  const handlePrevious = () => {
    setOffset(Math.max(0, offset - limit));
  };

  const handleNext = () => {
    setOffset(offset + limit);
  };

  // Handle quick actions - USANDO MUTACIONES REALES CON MODALES
  const openInvoiceDialog = (milestone: BillingMilestone) => {
    setInvoicingMilestone(milestone);
    setInvoiceNumber(milestone.invoiceNumber || "");
    setInvoiceDate(
      milestone.invoiceDate
        ? new Date(milestone.invoiceDate).toISOString().split("T")[0]
        : ""
    );
    const plannedBase = resolvePlannedBillingMilestoneAmount(milestone);
    setInvoiceTaxableBase(milestone.invoiceTaxableBase ?? String(plannedBase ?? ""));
    setInvoiceTaxAmount(milestone.invoiceTaxAmount ?? "");
    setInvoiceVatRate("manual");
    setInvoiceWithholdingAmount(milestone.invoiceWithholdingAmount ?? "");
    setInvoiceError(null);
  };

  const closeInvoiceDialog = () => {
    if (markInvoicedMutation.isPending) return;
    setInvoicingMilestone(null);
    setInvoiceError(null);
  };

  const handleMarkInvoicedSubmit = async () => {
    if (!invoicingMilestone) return;
    if (!invoiceNumber.trim()) {
      setInvoiceError("Indica el número de factura.");
      return;
    }
    if (!invoiceDate.trim()) {
      setInvoiceError("Indica la fecha de la factura.");
      return;
    }
    const base = parseSettlementMoney(invoiceTaxableBase);
    const tax = parseSettlementMoney(invoiceTaxAmount);
    const withholding = parseSettlementMoney(invoiceWithholdingAmount);
    if (base === null || base <= 0 || tax === null || tax < 0 || withholding === null || withholding < 0 || base + tax - withholding <= 0) {
      setInvoiceError("Indica base imponible, IVA y retención válidos. Usa 0,00 cuando no corresponda.");
      return;
    }
    setInvoiceError(null);
    try {
      await markInvoicedMutation.mutateAsync({
        id: invoicingMilestone.id,
        invoiceNumber: invoiceNumber.trim(),
        invoiceDate: new Date(invoiceDate),
        invoiceTaxableBase: base.toFixed(2),
        invoiceTaxAmount: tax.toFixed(2),
        invoiceWithholdingAmount: withholding.toFixed(2),
        invoiceAmount: (base + tax - withholding).toFixed(2),
      });
      closeInvoiceDialog();
      await milestonesQuery.refetch();
      toast.success("Hito marcado como facturado");
    } catch (error: any) {
      setInvoiceError(error?.message || "Error al marcar como facturado");
    }
  };

  const handleMarkSentToClient = async (milestoneId: number) => {
    try {
      await markSentToClientMutation.mutateAsync({ id: milestoneId });
      await milestonesQuery.refetch();
      toast.success("Envío al cliente registrado; sin envío automático de correo");
    } catch (error) { toast.error("Error al registrar el envío al cliente"); }
  };

  const handleMarkPendingCollection = async (milestoneId: number) => {
    try {
      await markPendingCollectionMutation.mutateAsync({ id: milestoneId });
      await milestonesQuery.refetch();
      toast.success("Hito marcado como pendiente de cobro");
    } catch (error) {
      toast.error("Error al marcar como pendiente de cobro");
    }
  };

  const openCollectionDialog = (milestone: BillingMilestone) => {
    setSettlementMilestone(milestone);
    setSettlementDate(new Date().toISOString().slice(0, 10));
    setSettlementGross("");
    setSettlementMethod(milestone.effectivePaymentMethod ?? milestone.paymentMethod ?? "transfer");
    setSettlementReference("");
    setSettlementIdempotencyKey(crypto.randomUUID());
    setSettlementCommission("0.00");
    setSettlementInterest("0.00");
    setSettlementError(null);
  };

  const closeConfirmingSettlementDialog = () => {
    if (recordCollectionMutation.isPending) return;
    setSettlementMilestone(null);
    setSettlementError(null);
  };

  const confirmingFinancialCost = () => {
    const commission = settlementCommission === "" ? 0 : parseSettlementMoney(settlementCommission);
    const interest = settlementInterest === "" ? 0 : parseSettlementMoney(settlementInterest);
    return commission === null || interest === null ? null : commission + interest;
  };

  const confirmingNetReceived = () => {
    const gross = parseSettlementMoney(settlementGross);
    const cost = confirmingFinancialCost();
    return gross === null || cost === null ? null : gross - cost;
  };

  const handleCollectionSubmit = async () => {
    if (!settlementMilestone) return;
    const isConfirming = settlementMethod === "confirming";

    const gross = parseSettlementMoney(settlementGross);
    if (!settlementDate) {
      setSettlementError("Indica la fecha de cobro.");
      return;
    }
    if (gross === null || gross <= 0) {
      setSettlementError("El importe bruto satisfecho debe ser mayor que cero.");
      return;
    }
    if (settlementMilestone.invoiceFiscalVerified !== true) {
      setSettlementError("La factura no tiene un desglose fiscal verificable. Completa o revisa sus datos antes de registrar cobros.");
      return;
    }

    let commission = 0;
    let interest = 0;
    if (isConfirming) {
      commission = settlementCommission === "" ? 0 : parseSettlementMoney(settlementCommission) ?? 0;
      interest = settlementInterest === "" ? 0 : parseSettlementMoney(settlementInterest) ?? 0;
      if (commission < 0 || interest < 0) {
        setSettlementError("La comisión y los intereses deben ser importes válidos no negativos.");
        return;
      }
      if (commission + interest > gross) {
        setSettlementError("La suma de comisión e intereses no puede superar el importe bruto satisfecho.");
        return;
      }
    }

    const balance = parseSettlementMoney(settlementMilestone.balanceAmount ?? "");
    if (balance === null || balance <= 0 || Math.round(gross * 100) > Math.round(balance * 100)) {
      setSettlementError(balance !== null && balance > 0
        ? "El cobro supera el saldo exigible; el excedente no se aplicará automáticamente."
        : "No existe saldo exigible verificable en una factura pendiente.");
      return;
    }

    setSettlementError(null);
    try {
      const result = await recordCollectionMutation.mutateAsync({
        billingMilestoneId: settlementMilestone.id,
        occurredAt: new Date(`${settlementDate}T12:00:00`),
        amount: gross.toFixed(2),
        paymentMethod: settlementMethod as "transfer" | "confirming" | "promissory_note" | "other" | "cash",
        reference: settlementReference.trim() || undefined,
        idempotencyKey: settlementIdempotencyKey,
        ...(isConfirming
          ? {
              bankCommissionAmount: settlementCommission || "0.00",
              bankInterestAmount: settlementInterest || "0.00",
            }
          : {}),
      });
      closeConfirmingSettlementDialog();
      await milestonesQuery.refetch();
      toast.success(Number(result.balanceAmount) > 0
        ? "Cobro parcial registrado; la factura conserva saldo pendiente."
        : result.invoiceEconomicReviewRequired
          ? "Cobro registrado; la diferencia económica requiere revisión antes de cerrar el hito."
          : "Factura liquidada y hito cerrado.");
    } catch (error: any) {
      setSettlementError(error?.message || "Error al registrar el cobro.");
    }
  };

  const handleMarkCollected = (milestone: BillingMilestone) => {
    openCollectionDialog(milestone);
  };

  const handleOpenInstallation = (installationId: number) => {
    window.location.href = `/installations/${installationId}`;
  };

  const handleOpenBillingTab = (installationId: number) => {
    window.location.href = `/installations/${installationId}?tab=billing`;
  };

  const toggleMilestoneDetail = (milestoneId: number) => {
    setExpandedMilestones((current) => {
      const next = new Set(current);
      if (next.has(milestoneId)) {
        next.delete(milestoneId);
      } else {
        next.add(milestoneId);
      }
      return next;
    });
  };

  if (!isManager) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-700">Acceso denegado</h3>
          <p className="text-gray-500 mt-2">No tienes permiso para acceder a este dashboard</p>
        </div>
      </div>
    );
  }

  if (milestonesQuery.isLoading) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  if (milestonesQuery.isError) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-700">Error al cargar los datos</h3>
          <p className="text-gray-500 mt-2">{milestonesQuery.error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-w-0 max-w-full space-y-6 overflow-x-hidden">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{pageCopy.title}</h1>
          <p className="text-gray-500 mt-1">{pageCopy.description}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => milestonesQuery.refetch()}>
            <Clock className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <FinanceMetricGrid maxColumns={4}>
        {primaryFinanceKpis.map((kpi) => (
          <FinanceMetricCard
            key={kpi.key}
            title={kpi.title}
            amountFormatted={formatEuroAmount(kpi.amount)}
            count={kpi.count}
            countLabel="hitos"
            description={kpi.description}
            icon={kpi.icon}
            variant={kpi.variant}
          />
        ))}
      </FinanceMetricGrid>

      <>
        {(unknownInvoiceAmountCount > 0 || unknownCollectedAmountCount > 0 || collectedLedgerConflictCount > 0 || collectedBankUnverifiedCount > 0 || (financialTotals?.collected?.count ?? 0) > 0) ? (
          <div role="status" data-testid="finance-historical-coverage-notice" className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950">
            <p className="font-medium">Cobertura y conciliación histórica</p>
            {(financialTotals?.collected?.count ?? 0) > 0 ? <p className="mt-1">Procedencia de cobros: {formatEuroAmount(collectedLedgerAmountCents / 100)} en movimientos del ledger y {formatEuroAmount(collectedLegacyPaymentSnapshotAmountCents / 100)} en importes históricos anteriores al ledger.</p> : null}
            {collectedBankUnverifiedCount > 0 ? <p className="mt-1">{collectedBankUnverifiedCount} factura(s) cobradas conservan el estado cerrado, pero su importe bancario no está conciliado como movimiento; no se crea saldo ni cobro a partir del IVA.</p> : null}
            {(financialTotals?.invoiced?.count ?? 0) > 0 ? <p className="mt-1">La base económica facturada se informa sin IVA; el exigible, el IVA repercutido y la retención fiscal se muestran en dimensiones separadas.</p> : null}
            {(financialTotals?.invoicePayable?.count ?? 0) > 0 ? <p className="mt-1">IVA repercutido verificable: {formatEuroAmount(invoiceTaxAmountCents / 100)}. Retención fiscal de factura: {formatEuroAmount(invoiceWithholdingAmountCents / 100)}.</p> : null}
            {unknownInvoiceAmountCount > 0 ? <p className="mt-1">{unknownInvoiceAmountCount} hito(s) conservan su estado de factura, pero su importe fiscal no es verificable y no se incluye en la base ni en el exigible acumulado.</p> : null}
            {unknownCollectedAmountCount > 0 ? <p className="mt-1">{unknownCollectedAmountCount} hito(s) permanecen como cobrados; falta un importe de cobro acreditado y no se estima.</p> : null}
            {collectedLedgerConflictCount > 0 ? <p className="mt-1">{collectedLedgerConflictCount} hito(s) con estado «Cobrado» muestran un saldo de ledger discrepante. Se conserva el estado cerrado y no se convierten automáticamente en deuda; requieren conciliación.</p> : null}
          </div>
        ) : null}
        <FinanceSecondaryMetrics title="Acumulados y otros estados" metrics={secondaryFinanceKpis} />
      </>

      {/* Filters */}
      {/* Filters */}
      <div className="card p-4 rounded-lg border border-gray-200">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="grid gap-2">
            <Label htmlFor="status" className="text-xs font-medium text-gray-700">
              Estado
            </Label>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button id="status" type="button" variant="outline" className="w-full justify-between font-normal">
                  {statusFilterLabel}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-72">
                {STATUS_OPTIONS.map((option) => (
                  <DropdownMenuCheckboxItem
                    key={option.value}
                    checked={statusFilters.includes(option.value)}
                    onCheckedChange={() => toggleStatusFilter(option.value)}
                    onSelect={(event) => event.preventDefault()}
                  >
                    {option.label}
                  </DropdownMenuCheckboxItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={() => {
                    setOffset(0);
                    setStatusFilters([]);
                  }}
                >
                  Limpiar selección
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="search" className="text-xs font-medium text-gray-700">
              Buscar
            </Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                id="search"
                type="text"
                placeholder="Cliente, instalación, factura..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="clientId" className="text-xs font-medium text-gray-700">
              Cliente ID
            </Label>
            <Input
              id="clientId"
              type="text"
              placeholder="ID cliente"
              value={clientIdFilter}
              onChange={(e) => setClientIdFilter(e.target.value)}
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="type" className="text-xs font-medium text-gray-700">
              Tipo
            </Label>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger id="type">
                <SelectValue placeholder="Todos los tipos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los tipos</SelectItem>
                <SelectItem value="fixed_amount">Importe fijo</SelectItem>
                <SelectItem value="percentage">Porcentaje</SelectItem>
                <SelectItem value="monthly_certification">Certificación mensual</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="overdue" className="text-xs font-medium text-gray-700">
              Vencido
            </Label>
            <Select value={overdueFilter} onValueChange={setOverdueFilter}>
              <SelectTrigger id="overdue">
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="yes">Sí (vencido)</SelectItem>
                <SelectItem value="no">No (no vencido)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="dateFrom" className="text-xs font-medium text-gray-700">
              Desde
            </Label>
            <Input
              id="dateFrom"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="dateTo" className="text-xs font-medium text-gray-700">
              Hasta
            </Label>
            <Input
              id="dateTo"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </div>

          <div className="flex items-end">
            <Button type="button" variant="outline" className="w-full" onClick={clearAllFilters}>
              <X className="mr-2 h-4 w-4" />
              Limpiar filtros
            </Button>
          </div>
        </div>
      </div>

      {/* Main list: mobile cards + desktop table */}
      <div className="card rounded-lg border border-gray-200 overflow-hidden">
        <div className="md:hidden">
          <div className="space-y-3 p-3">
            {filteredMilestones.length === 0 ? (
              <div className="rounded-lg border border-dashed border-gray-200 px-4 py-8 text-center text-sm text-gray-500">
                No se encontraron hitos de facturación
              </div>
            ) : (
              displayedMilestones.map((milestone: BillingMilestone) => {
                const invoicePaidPendingReview = isInvoicePaidPendingEconomicReview(milestone);
                const statusBadge = getStatusBadge(milestone, invoicePaidPendingReview);
                const realDueDate = calculateRealDueDate(milestone);
                const verifiedOverdue = isBillingInvoiceOverdue(milestone);
                const daysOverdue = verifiedOverdue ? milestone.daysOverdue : 0;
                const isOverdue = invoicePaidPendingReview ? false : verifiedOverdue;
                const isExpanded = expandedMilestones.has(milestone.id);

                return (
                  <Card key={milestone.id} className="overflow-hidden border-gray-200 shadow-sm">
                    <CardContent className="space-y-4 p-4">
                      <div className="flex items-start justify-between gap-3">
                        <Badge variant="outline" className={`${statusBadge.className} max-w-[70%] whitespace-normal text-left leading-tight`} data-testid={invoicePaidPendingReview ? "invoice-paid-economic-review-status" : undefined}>
                          {statusBadge.label}
                        </Badge>
                        <div className="shrink-0 text-right text-base font-semibold text-gray-900">
                          {mode === "income"
                            ? (milestone.invoiceAmount != null ? formatEconomicReviewAmount(Number(milestone.invoiceAmount)) : "—")
                            : <>{invoicePaidPendingReview ? <span className="text-xs font-medium">Planificado: </span> : null}{formatMilestoneAmount(milestone)}</>}
                        </div>
                      </div>
                      <div className="space-y-3 text-sm">
                        {mode === "income" ? (
                          <>
                            <MobileField label="N.º de factura">
                              {milestone.invoiceNumber ? (
                                milestone.invoiceDocumentUrl ? (
                                  <a href={milestone.invoiceDocumentUrl} target="_blank" rel="noopener noreferrer" className="font-medium text-ods-navigation underline underline-offset-2">
                                    {milestone.invoiceNumber}
                                  </a>
                                ) : <span className="break-words [overflow-wrap:anywhere]">{milestone.invoiceNumber}</span>
                              ) : "—"}
                            </MobileField>
                            <MobileField label="Cliente">
                              <span className="font-medium text-gray-900 break-words">{milestone.clientName}</span>
                            </MobileField>
                            <MobileField label="Instalación">
                              <div className="min-w-0">
                                <p className="font-medium text-gray-900 break-words">{milestone.installationName || "—"}</p>
                                <p className="text-xs text-gray-500" data-testid="income-installation-expediente">
                                  {milestone.installationCode ? `Exp: ${milestone.installationCode}` : "—"}
                                </p>
                              </div>
                            </MobileField>
                            <MobileField label="Concepto">
                              <span className="font-medium text-gray-900 break-words [overflow-wrap:anywhere]">{milestone.label || "Sin concepto"}</span>
                            </MobileField>
                            <MobileField label="Base imponible">{formatFiscalBreakdownAmount(milestone.invoiceTaxableBase)}</MobileField>
                            <MobileField label="Importe total de la factura">
                              {milestone.invoiceAmount != null ? formatEconomicReviewAmount(Number(milestone.invoiceAmount)) : "—"}
                            </MobileField>
                            <MobileField label="Fecha de vencimiento">
                              <div className="space-y-0.5">
                                <span className={isOverdue ? "font-semibold text-red-600" : "text-gray-900"} data-testid="income-invoice-due-date">
                                  {realDueDate ? realDueDate.toLocaleDateString("es-ES") : "—"}
                                  {isOverdue && daysOverdue > 0 ? ` · ${daysOverdue} días vencido` : ""}
                                </span>
                                {renderIncomeCollectionMovements(milestone)}
                              </div>
                            </MobileField>
                          </>
                        ) : (
                          <>
                        <MobileField label="Cliente / Expediente">
                          <div className="min-w-0">
                            <p className="font-medium text-gray-900 break-words">{milestone.clientName}</p>
                            <p className="text-xs text-gray-500">{milestone.clientId}</p>
                          </div>
                        </MobileField>

                        <MobileField label="Obra / Instalación">
                          <div className="min-w-0">
                            <p className="font-medium text-gray-900 break-words">{milestone.installationName}</p>
                            <p className="text-xs text-gray-500">ID: {milestone.installationId}</p>
                          </div>
                        </MobileField>

                        <MobileField label="Orden">
                          {getWorkOrderTypeLabel(milestone.workOrderType)}
                        </MobileField>

                        <MobileField label="Concepto">
                          <div className="min-w-0 space-y-1">
                            <p className="font-medium text-gray-900 break-words [overflow-wrap:anywhere]">{milestone.label || "Sin concepto"}</p>
                            {milestone.description ? (
                              <p className="line-clamp-2 text-xs text-gray-500 break-words [overflow-wrap:anywhere]">{milestone.description}</p>
                            ) : null}
                          </div>
                        </MobileField>

                        <MobileField label={invoicePaidPendingReview ? "Planificado" : "Importe"}>
                          <span className="font-medium text-gray-900">
                            {formatMilestoneAmount(milestone)}
                          </span>
                        </MobileField>

                        {invoicePaidPendingReview ? (
                          <MobileField label="Factura / cobro">
                            {renderInvoiceEconomicReviewAmounts(milestone)}
                          </MobileField>
                        ) : null}

                        {milestone.status === "collected" ? (
                          <MobileField label="Cobro registrado">
                            {renderRecordedCollectionAmount(milestone)}
                          </MobileField>
                        ) : null}

                        <MobileField label="Fecha prevista">
                          {milestone.expectedInvoiceDate ? new Date(milestone.expectedInvoiceDate).toLocaleDateString("es-ES") : "-"}
                        </MobileField>

                        {isInvoiceCollectionComplete(milestone) ? (
                          <MobileField label="Cobro">
                            <span className="text-gray-600 text-xs">
                              {getCollectedPaymentLabel(milestone)}
                            </span>
                          </MobileField>
                        ) : (
                          <MobileField label="Vencimiento / Días vencido">
                            <span className={isOverdue ? "font-semibold text-red-600" : "text-gray-900"}>
                              {realDueDate ? realDueDate.toLocaleDateString("es-ES") : "-"}
                              {isOverdue && daysOverdue > 0 ? ` · ${daysOverdue} días vencido` : ""}
                            </span>
                          </MobileField>
                        )}

                        {milestone.invoiceNumber ? (
                          <MobileField label="Nº factura">
                            <span className="break-words [overflow-wrap:anywhere]">{milestone.invoiceNumber}</span>
                          </MobileField>
                        ) : null}

                        <MobileField label="Último aviso">
                          {renderNoticeSummary(milestone.noticeSummary, true)}
                        </MobileField>
                          </>
                        )}
                      </div>

                      <div className="grid grid-cols-1 gap-2 pt-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => toggleMilestoneDetail(milestone.id)}
                          aria-expanded={isExpanded}
                          aria-label={isExpanded ? "Ocultar detalle" : "Mostrar detalle"}
                          className="w-full justify-center"
                        >
                          {isExpanded ? <ChevronUp className="w-4 h-4 mr-1" /> : <ChevronDown className="w-4 h-4 mr-1" />}
                          Detalle
                        </Button>
                        {milestone.installationId != null && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleOpenBillingTab(milestone.installationId!)}
                          className="w-full justify-center"
                        >
                          <FileText className="w-4 h-4 mr-1" />
                          Abrir Facturación
                        </Button>
                        )}
                      </div>

                      {isExpanded ? (
                        <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50/70 p-3">
                          <div className="grid gap-3">
                            <DetailItem label="Hito / tipo" value={milestone.typeLabel} />
                            <DetailItem label="Orden" value={getWorkOrderTypeLabel(milestone.workOrderType)} />
                            <DetailItem label="Factura" value={milestone.invoiceNumber} />
                            <DetailItem label="Fecha factura" value={milestone.invoiceDate ? new Date(milestone.invoiceDate).toLocaleDateString("es-ES") : null} />
                            <DetailItem label="Fecha prevista" value={milestone.expectedInvoiceDate ? new Date(milestone.expectedInvoiceDate).toLocaleDateString("es-ES") : null} />
                            {isInvoiceCollectionComplete(milestone) ? (
                              <DetailItem
                                label="Fecha cobro"
                                value={milestone.paymentDate
                                  ? getCollectedPaymentLabel(milestone)
                                  : "Cobrado · sin fecha registrada"}
                              />
                            ) : (
                              <DetailItem label="Fecha vencimiento" value={realDueDate ? realDueDate.toLocaleDateString("es-ES") : null} />
                            )}
                            <DetailItem label="Aviso contabilidad" value={milestone.accountingNotifiedAt ? new Date(milestone.accountingNotifiedAt).toLocaleString("es-ES") : null} />
                            <DetailItem label="Último aviso" value={renderNoticeSummary(milestone.noticeSummary, true)} />
                            <DetailItem label="Cliente / Expediente" value={`${milestone.clientName} (${milestone.clientId})`} />
                            <DetailItem label="Plazo pago" value={milestone.paymentTerms ? `${milestone.paymentTerms} días` : null} />
                            <DetailItem label="Creado" value={milestone.createdAt ? new Date(milestone.createdAt).toLocaleDateString("es-ES") : null} />
                            <DetailItem label="Actualizado" value={milestone.updatedAt ? new Date(milestone.updatedAt).toLocaleDateString("es-ES") : null} />
                          </div>
                          {milestone.description ? (
                            <div className="rounded-md border border-gray-200 bg-white p-3">
                              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Descripción completa</p>
                              <p className="mt-1 text-sm text-gray-800 whitespace-pre-wrap break-words [overflow-wrap:anywhere]">{milestone.description}</p>
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        </div>

        <div className="hidden min-h-[360px] max-h-[calc(100vh-360px)] overflow-x-auto overflow-y-auto md:block">
          <Table className={`${mode === "income" ? "min-w-[1570px]" : "min-w-[1450px]"} table-fixed`}>
            <TableHeader className="sticky top-0 z-10 bg-background">
              <TableRow>
                {mode === "income" ? (
                  <>
                    {renderIncomeSortHeader("status", "Estado de la factura", "w-[150px]")}
                    {renderIncomeSortHeader("invoiceNumber", "N.º de factura", "w-[140px]")}
                    {renderIncomeSortHeader("client", "Cliente", "w-[190px]")}
                    {renderIncomeSortHeader("installation", "Instalación", "w-[220px]")}
                    {renderIncomeSortHeader("concept", "Concepto", "w-[220px]")}
                    {renderIncomeSortHeader("taxableBase", "Base imponible", "w-[140px] text-right")}
                    {renderIncomeSortHeader("invoiceTotal", "Importe total", "w-[140px] text-right")}
                    {renderIncomeSortHeader("dueDate", "Fecha de vencimiento", "w-[150px]")}
                    <TableHead className="w-[220px] text-right">Acciones</TableHead>
                  </>
                ) : (
                  <>
                    <TableHead className="w-[150px]">Estado</TableHead>
                    <TableHead className="w-[190px]">Cliente / Expediente</TableHead>
                    <TableHead className="w-[220px]">Obra / Instalación</TableHead>
                    <TableHead className="w-[270px]">Concepto</TableHead>
                    <TableHead className="w-[120px]">Orden</TableHead>
                    <TableHead className="w-[130px] text-right">Importe</TableHead>
                    <TableHead className="w-[220px]">Fecha / Vencimiento</TableHead>
                    <TableHead className="w-[150px] text-right">Detalle / acciones</TableHead>
                  </>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredMilestones.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={mode === "income" ? 9 : 8} className="text-center py-8 text-gray-500">
                    No se encontraron hitos de facturación
                  </TableCell>
                </TableRow>
              ) : (
                displayedMilestones.map((milestone: BillingMilestone) => {
                  const invoicePaidPendingReview = isInvoicePaidPendingEconomicReview(milestone);
                  const statusBadge = getStatusBadge(milestone, invoicePaidPendingReview);
                  const realDueDate = calculateRealDueDate(milestone);
                  const verifiedOverdue = isBillingInvoiceOverdue(milestone);
                  const daysOverdue = verifiedOverdue ? milestone.daysOverdue : 0;
                  const isOverdue = invoicePaidPendingReview ? false : verifiedOverdue;
                  const isExpanded = expandedMilestones.has(milestone.id);

                  return (
                    <React.Fragment key={milestone.id}>
                      <TableRow className="align-top">
                        <TableCell className="w-[150px] max-w-[150px]">
                          <Badge variant="outline" className={`${statusBadge.className} whitespace-normal text-center leading-tight`} data-testid={invoicePaidPendingReview ? "invoice-paid-economic-review-status" : undefined}>
                            {statusBadge.label}
                          </Badge>
                        </TableCell>
                        {mode === "income" ? (
                          <>
                            <TableCell className="w-[140px] max-w-[140px] whitespace-normal">
                              {milestone.invoiceNumber ? (
                                milestone.invoiceDocumentUrl ? (
                                  <a href={milestone.invoiceDocumentUrl} target="_blank" rel="noopener noreferrer" className="flex min-w-0 max-w-full items-start gap-1 whitespace-normal font-medium text-ods-navigation underline underline-offset-2" aria-label={`Abrir factura ${milestone.invoiceNumber}`}>
                                    <FileText className="h-3.5 w-3.5 shrink-0" />
                                    <span className="min-w-0 max-w-full overflow-hidden line-clamp-2 break-all" title={milestone.invoiceNumber}>{milestone.invoiceNumber}</span>
                                  </a>
                                ) : <span className="block min-w-0 max-w-full overflow-hidden line-clamp-2 break-all" title={milestone.invoiceNumber}>{milestone.invoiceNumber}</span>
                              ) : "—"}
                            </TableCell>
                            <TableCell className="w-[190px] max-w-[190px] whitespace-normal">
                              <span className="block min-w-0 max-w-full overflow-hidden whitespace-normal line-clamp-2 break-words [overflow-wrap:anywhere] font-medium" title={milestone.clientName}>{milestone.clientName}</span>
                            </TableCell>
                            <TableCell className="w-[220px] max-w-[220px] whitespace-normal">
                              <div className="flex min-w-0 flex-col gap-1 whitespace-normal">
                                <span className="block min-w-0 max-w-full overflow-hidden whitespace-normal line-clamp-2 break-words [overflow-wrap:anywhere] font-medium" title={milestone.installationName ?? undefined}>{milestone.installationName || "—"}</span>
                                <span className="block min-w-0 max-w-full truncate text-xs text-gray-500" title={milestone.installationCode ? `Exp: ${milestone.installationCode}` : undefined} data-testid="income-installation-expediente">
                                  {milestone.installationCode ? `Exp: ${milestone.installationCode}` : "—"}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell className="w-[220px] max-w-[220px] whitespace-normal">
                              <span className="block min-w-0 max-w-full overflow-hidden whitespace-normal line-clamp-2 break-words [overflow-wrap:anywhere] font-medium" title={milestone.label || undefined}>{milestone.label || "Sin concepto"}</span>
                            </TableCell>
                            <TableCell className="w-[140px] max-w-[140px] text-right font-medium">
                              {formatFiscalBreakdownAmount(milestone.invoiceTaxableBase)}
                            </TableCell>
                            <TableCell className="w-[140px] max-w-[140px] text-right font-medium">
                              {milestone.invoiceAmount != null ? formatEconomicReviewAmount(Number(milestone.invoiceAmount)) : "—"}
                            </TableCell>
                          </>
                        ) : (
                          <>
                        <TableCell className="w-[190px] max-w-[190px]">
                          <div className="flex min-w-0 flex-col gap-1">
                            <span className="truncate font-medium" title={milestone.clientName}>{milestone.clientName}</span>
                            <span className="truncate text-xs text-gray-500" title={milestone.clientId}>{milestone.clientId}</span>
                          </div>
                        </TableCell>
                        <TableCell className="w-[220px] max-w-[220px]">
                          <div className="flex min-w-0 flex-col gap-1">
                            <span className="line-clamp-2 font-medium break-words [overflow-wrap:anywhere]" title={milestone.installationName ?? undefined}>{milestone.installationName}</span>
                            <span className="text-xs text-gray-500">ID: {milestone.installationId}</span>
                          </div>
                        </TableCell>
                        <TableCell className="w-[270px] max-w-[270px]">
                          <div className="flex min-w-0 flex-col gap-1 whitespace-normal break-words [overflow-wrap:anywhere]">
                            <span className="line-clamp-2 font-medium" title={milestone.label || undefined}>{milestone.label || "Sin concepto"}</span>
                            {milestone.description && (
                              <span className="text-xs text-gray-500 line-clamp-2" title={milestone.description}>{milestone.description}</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="w-[120px] max-w-[120px]">
                          <span className="text-sm text-gray-700">{getWorkOrderTypeLabel(milestone.workOrderType)}</span>
                        </TableCell>
                        <TableCell className="w-[170px] max-w-[170px] text-right font-medium">
                          {invoicePaidPendingReview ? <div className="text-xs font-normal text-gray-600">Planificado</div> : null}
                          <div>{formatMilestoneAmount(milestone)}</div>
                          {renderInvoiceEconomicReviewAmounts(milestone)}
                          {renderRecordedCollectionAmount(milestone)}
                        </TableCell>
                          </>
                        )}
                        <TableCell className="w-[220px] max-w-[220px]" data-testid={mode === "income" ? "income-due-date-cell" : undefined}>
                          {mode === "income" ? (
                            <div className="space-y-0.5">
                              <span className={isOverdue ? "font-semibold text-red-600" : "text-gray-700"} data-testid="income-invoice-due-date">
                                {realDueDate ? realDueDate.toLocaleDateString("es-ES") : "—"}
                                {isOverdue && daysOverdue > 0 ? <span className="block text-xs">{daysOverdue} días vencido</span> : null}
                              </span>
                              {renderIncomeCollectionMovements(milestone)}
                            </div>
                          ) : <div className="flex min-w-0 flex-col gap-1 text-sm">
                            <span>
                              Prevista: {milestone.expectedInvoiceDate ? new Date(milestone.expectedInvoiceDate).toLocaleDateString("es-ES") : "-"}
                            </span>
                            {isInvoiceCollectionComplete(milestone) ? (
                              <span className="text-xs text-gray-500">
                                {getCollectedPaymentLabel(milestone)}
                              </span>
                            ) : (
                              <>
                                <span className={isOverdue ? 'text-red-600 font-bold' : 'text-gray-600'}>
                                  Venc.: {realDueDate ? realDueDate.toLocaleDateString("es-ES") : "-"}
                                </span>
                                {isOverdue && daysOverdue > 0 ? (
                                  <span className="text-xs font-bold text-red-600">{daysOverdue} días vencido</span>
                                ) : null}
                              </>
                            )}
                            <div className="pt-1 text-xs text-gray-500">
                              {renderNoticeSummary(milestone.noticeSummary, true)}
                            </div>
                          </div>}
                        </TableCell>
                        <TableCell className={`${mode === "income" ? "w-[220px] max-w-[220px]" : "w-[150px] max-w-[150px]"} text-right`}>
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => toggleMilestoneDetail(milestone.id)}
                              aria-expanded={isExpanded}
                              aria-label={isExpanded ? "Ocultar detalle" : "Mostrar detalle"}
                              className="h-8 px-2 text-xs"
                            >
                              {isExpanded ? <ChevronUp className="w-4 h-4 mr-1" /> : <ChevronDown className="w-4 h-4 mr-1" />}
                              Detalle
                            </Button>
                            {milestone.installationId != null && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleOpenInstallation(milestone.installationId!)}
                              title="Abrir instalación"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            )}
                            {milestone.installationId != null && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleOpenBillingTab(milestone.installationId!)}
                              title="Abrir pestaña facturación"
                            >
                              <FileText className="w-4 h-4" />
                            </Button>
                            )}
                            {milestone.status === 'pending' || milestone.status === 'ready_to_invoice' ? (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => openInvoiceDialog(milestone)}
                                title="Marcar facturado"
                              >
                                <CheckCircle2 className="w-4 h-4 text-green-600" />
                              </Button>
                            ) : null}
                            {milestone.status === 'invoiced' && !canRecordCollection(milestone) ? (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleMarkSentToClient(milestone.id)}
                                title="Registrar envío al cliente"
                              >
                                <TrendingUp className="w-4 h-4 text-orange-600" />
                              </Button>
                            ) : null}
                            {milestone.status === 'sent_to_client' && !canRecordCollection(milestone) ? (
                              <Button variant="ghost" size="icon" onClick={() => handleMarkPendingCollection(milestone.id)} title="Marcar pendiente de cobro"><TrendingUp className="w-4 h-4 text-orange-600" /></Button>
                            ) : null}
                            {canRecordCollection(milestone) ? (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleMarkCollected(milestone)}
                                title="Registrar cobro"
                              >
                                <DollarSign className="w-4 h-4 text-green-600" />
                              </Button>
                            ) : null}
                            {isFinanceManager
                              && Boolean(milestone.invoiceNumber)
                              && milestone.invoiceFiscalVerified !== true
                              && ['invoiced', 'sent_to_client', 'pending_collection', 'collected'].includes(milestone.status) ? (
                              <Button variant="ghost" size="icon" onClick={() => openInvoiceDialog(milestone)} title="Completar desglose fiscal">
                                <AlertCircle className="w-4 h-4 text-amber-600" />
                              </Button>
                            ) : null}
                          </div>
                        </TableCell>
                      </TableRow>
                      {isExpanded ? (
                        <TableRow className="bg-gray-50/70">
                          <TableCell colSpan={mode === "income" ? 9 : 8} className="px-4 py-4">
                            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                              <DetailItem label="Hito / tipo" value={milestone.typeLabel} />
                              <DetailItem label="Orden" value={getWorkOrderTypeLabel(milestone.workOrderType)} />
                              <DetailItem label="Factura" value={milestone.invoiceNumber} />
                              <DetailItem label="Fecha factura" value={milestone.invoiceDate ? new Date(milestone.invoiceDate).toLocaleDateString("es-ES") : null} />
                              <DetailItem label="Desglose fiscal" value={milestone.invoiceFiscalVerified === true ? `Base ${formatFiscalBreakdownAmount(milestone.invoiceTaxableBase)} · IVA ${formatFiscalBreakdownAmount(milestone.invoiceTaxAmount)} · Retención ${formatFiscalBreakdownAmount(milestone.invoiceWithholdingAmount)}` : "Pendiente de verificar"} />
                              <DetailItem label="Saldo factura" value={milestone.balanceAmount != null ? formatEuroAmount(Number(milestone.balanceAmount)) : null} />
                              {milestone.invoiceEconomicReviewRequired ? <DetailItem label="Revisión económica" value={milestone.invoiceEconomicReviewMessage || "La base facturada difiere del plan"} /> : null}
                              <DetailItem label="Fecha prevista" value={milestone.expectedInvoiceDate ? new Date(milestone.expectedInvoiceDate).toLocaleDateString("es-ES") : null} />
                              {isInvoiceCollectionComplete(milestone) ? (
                                <DetailItem
                                  label="Fecha cobro"
                                  value={getCollectedPaymentLabel(milestone)}
                                />
                              ) : (
                                <DetailItem label="Fecha vencimiento" value={realDueDate ? realDueDate.toLocaleDateString("es-ES") : null} />
                              )}
                              <DetailItem label="Aviso contabilidad" value={milestone.accountingNotifiedAt ? new Date(milestone.accountingNotifiedAt).toLocaleString("es-ES") : null} />
                              <DetailItem label="Último aviso" value={renderNoticeSummary(milestone.noticeSummary, true)} />
                              <DetailItem label="Cliente" value={milestone.clientName} />
                              {milestone.contractCode ? (
                                <DetailItem label="Código contrato" value={milestone.contractCode} />
                              ) : null}
                              <DetailItem label="Expediente" value={milestone.installationCode || milestone.clientId} />
                              <DetailItem label="Hito" value={milestone.milestoneConcept || milestone.label} />
                              <DetailItem
                                label="Importe bruto"
                                value={(() => {
                                  const hasEvidence = hasRecordedCollectionEvidence(milestone);
                                  const paymentAmount = milestone.paymentAmount == null
                                    ? hasEvidence && milestone.collectedAmount != null
                                      ? parseSettlementMoney(String(milestone.collectedAmount))
                                      : null
                                    : parseSettlementMoney(String(milestone.paymentAmount));
                                  if (hasEvidence && paymentAmount !== null) return formatEconomicReviewAmount(paymentAmount);
                                  if (milestone.status === "collected") return "Importe no disponible";
                                  return milestone.amount > 0 ? formatEconomicReviewAmount(milestone.amount) : null;
                                })()}
                              />
                              <DetailItem label="Plazo pago" value={milestone.paymentTerms ? `${milestone.paymentTerms} días` : null} />
                              {milestone.bankCommissionAmount && milestone.bankCommissionAmount !== "0.00" ? (
                                <DetailItem label="Comisión bancaria" value={formatStoredEuroAmount(milestone.bankCommissionAmount)} />
                              ) : null}
                              {milestone.bankInterestAmount && milestone.bankInterestAmount !== "0.00" ? (
                                <DetailItem label="Intereses bancarios" value={formatStoredEuroAmount(milestone.bankInterestAmount)} />
                              ) : null}
                              {milestone.financialCostAmount && (hasRecordedCollectionEvidence(milestone) || (parseSettlementMoney(milestone.financialCostAmount) ?? 0) > 0) ? (
                                <DetailItem label="Coste financiero total" value={formatStoredEuroAmount(milestone.financialCostAmount)} />
                              ) : null}
                              {milestone.netReceivedAmount && (hasRecordedCollectionEvidence(milestone) || (parseSettlementMoney(milestone.netReceivedAmount) ?? 0) > 0) ? (
                                <DetailItem label="Neto recibido banco" value={formatStoredEuroAmount(milestone.netReceivedAmount)} />
                              ) : milestone.status === "collected" && !hasRecordedCollectionEvidence(milestone) ? (
                                <DetailItem label="Neto recibido banco" value="Importe no disponible" />
                              ) : null}
                              {milestone.collectionEvents?.length ? (
                                <div className="sm:col-span-2 lg:col-span-4 rounded-md border p-3 text-sm" data-testid="accounting-collection-history">
                                  <div className="mb-2 font-medium">Movimientos de cobro</div>
                                  <ul className="space-y-1">
                                    {milestone.collectionEvents.map((event, index) => (
                                      <li key={String(event.id ?? index)}>
                                        {String(event.eventType) === "reversal" ? "Reverso" : "Cobro"} · {formatEuroAmount(Number(event.amount))} · {String(event.occurredAt ?? "Fecha no disponible").slice(0, 10)}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              ) : null}
                              <DetailItem label="Creado" value={milestone.createdAt ? new Date(milestone.createdAt).toLocaleDateString("es-ES") : null} />
                              <DetailItem label="Actualizado" value={milestone.updatedAt ? new Date(milestone.updatedAt).toLocaleDateString("es-ES") : null} />
                            </div>
                            {milestone.description ? (
                              <div className="mt-3 rounded-md border border-gray-200 bg-white p-3">
                                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Descripción completa</p>
                                <p className="mt-1 text-sm text-gray-800 whitespace-pre-wrap break-words">{milestone.description}</p>
                              </div>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      ) : null}
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
          <div className="text-sm text-gray-500">
            Mostrando {filteredMilestones.length} de {totalMilestones} hitos
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrevious}
              disabled={!canPrevious}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleNext}
              disabled={!canNext}
            >
              Siguiente
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </div>

      {/* Diálogo de facturación */}
      <Dialog
        open={invoicingMilestone !== null}
        onOpenChange={(open) => {
          if (!open) closeInvoiceDialog();
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Marcar hito como facturado</DialogTitle>
            <DialogDescription>
              Introduce el número y la fecha de factura para este hito.
            </DialogDescription>
          </DialogHeader>
          {invoicingMilestone ? (
            <div className="space-y-4 py-2">
              <div className="rounded-md border bg-muted/30 p-3 text-sm">
                <div className="font-medium">
                  {invoicingMilestone.label || invoicingMilestone.description || "Hito de facturación"}
                </div>
                <div className="text-muted-foreground">
                  Cliente: {invoicingMilestone.clientName || invoicingMilestone.clientId}
                </div>
                <div>Importe aprobado: {formatEuroAmount(invoicingMilestone.amount)}</div>
                {invoicingMilestone.invoiceAmount != null ? <div>Factura registrada: {formatEuroAmount(Number(invoicingMilestone.invoiceAmount))}</div> : null}
                {(() => {
                  const base = parseSettlementMoney(invoiceTaxableBase);
                  const tax = parseSettlementMoney(invoiceTaxAmount);
                  const withholding = parseSettlementMoney(invoiceWithholdingAmount);
                  return base !== null && tax !== null && withholding !== null
                    ? <div data-testid="accounting-invoice-operation">Total exigible: {formatEuroAmount(base + tax - withholding)}</div>
                    : null;
                })()}
              </div>
              <div className="grid gap-2">
                <Label htmlFor="accountingInvoiceNumber">Número de factura *</Label>
                <Input
                  id="accountingInvoiceNumber"
                  placeholder="Ej: F2026-001"
                  value={invoiceNumber}
                  onChange={(event) => setInvoiceNumber(event.target.value)}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="accountingInvoiceDate">Fecha de factura *</Label>
                <Input
                  id="accountingInvoiceDate"
                  type="date"
                  value={invoiceDate}
                  onChange={(event) => setInvoiceDate(event.target.value)}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="accountingInvoiceTaxableBase">Base imponible (€) *</Label>
                <Input id="accountingInvoiceTaxableBase" type="number" min="0" step="0.01" value={invoiceTaxableBase} onChange={(event) => handleInvoiceBaseChange(event.target.value)} />
              </div>
              <div className="grid min-w-0 gap-4 sm:grid-cols-2">
                <div className="grid min-w-0 gap-2">
                  <Label htmlFor="accountingInvoiceVatRate">Tipo de IVA</Label>
                  <BillingVatRateSelect id="accountingInvoiceVatRate" value={invoiceVatRate} onValueChange={handleInvoiceVatRateChange} />
                  <Label htmlFor="accountingInvoiceTax">Cuota IVA (€) *</Label>
                  <Input id="accountingInvoiceTax" type="number" min="0" step="0.01" value={invoiceTaxAmount} onChange={(event) => setInvoiceTaxAmount(event.target.value)} />
                </div>
                <div className="grid min-w-0 gap-2">
                  <Label htmlFor="accountingInvoiceWithholding">Retención (€) *</Label>
                  <Input id="accountingInvoiceWithholding" type="number" min="0" step="0.01" value={invoiceWithholdingAmount} onChange={(event) => setInvoiceWithholdingAmount(event.target.value)} />
                </div>
              </div>
              {invoiceError ? (
                <p className="text-sm text-destructive" role="alert">{invoiceError}</p>
              ) : null}
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={closeInvoiceDialog} disabled={markInvoicedMutation.isPending}>
              Cancelar
            </Button>
            <Button onClick={handleMarkInvoicedSubmit} disabled={markInvoicedMutation.isPending || !invoiceNumber.trim() || !invoiceDate.trim()}>
              {markInvoicedMutation.isPending ? "Guardando..." : "Marcar facturado"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diálogo de cobro */}
      <Dialog
        open={settlementMilestone !== null}
        onOpenChange={(open) => {
          if (!open) closeConfirmingSettlementDialog();
        }}
      >
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>
              {settlementMethod === "confirming"
                ? "Registrar cobro Confirming"
                : "Registrar cobro"}
            </DialogTitle>
            <DialogDescription>
              {settlementMethod === "confirming"
                ? "El importe satisfecho del cliente es el bruto. La comisión y los intereses se registran como coste financiero y el neto bancario se calcula automáticamente."
                : "Introduce la fecha y el importe cobrado para este hito."}
            </DialogDescription>
          </DialogHeader>
          {settlementMilestone ? (
            <div className="space-y-4 py-2">
              <div className="rounded-md border bg-muted/30 p-3 text-sm">
                <div className="font-medium">
                  {settlementMilestone.label || settlementMilestone.description || "Hito de facturación"}
                </div>
                <div className="text-muted-foreground">
                  Cliente: {settlementMilestone.clientName || settlementMilestone.clientId}
                </div>
                <div>Importe aprobado: {formatEuroAmount(settlementMilestone.amount)}</div>
                {settlementMilestone.invoiceAmount != null ? <div>Factura exigible: {formatEuroAmount(Number(settlementMilestone.invoiceAmount))}</div> : <div>Factura no registrada</div>}
                <div>Cobrado antes: {formatEuroAmount(Number(settlementMilestone.collectedAmount ?? 0))}</div>
                <div>Importe de esta operación: {formatEuroAmount(parseSettlementMoney(settlementGross) ?? 0)}</div>
                {(() => {
                  const balanceBefore = parseSettlementMoney(settlementMilestone.balanceAmount ?? "");
                  const paid = parseSettlementMoney(settlementGross);
                  if (paid === null || balanceBefore === null) return null;
                  const balance = Math.round(balanceBefore * 100) - Math.round(paid * 100);
                  return <div data-testid="accounting-collection-balance">
                    {settlementMilestone.invoiceFiscalVerified !== true ? "No se puede registrar el cobro hasta revisar el desglose fiscal de la factura."
                      : balance > 0 ? `Saldo posterior: ${formatEuroAmount(balance / 100)}. Se registrará como parcial y la factura seguirá pendiente.`
                      : balance < 0 ? "El cobro supera el importe exigible. Revisa el importe; no se aplicará automáticamente el excedente."
                        : settlementMilestone.invoiceEconomicReviewRequired ? "Factura cobrada; el hito seguirá pendiente de revisión económica."
                          : "Este cobro liquida la factura."}
                  </div>;
                })()}
              </div>
              <div className="grid gap-2">
                <Label htmlFor="accountingCollectionMethod">Método real del cobro *</Label>
                <Select value={settlementMethod} onValueChange={setSettlementMethod}>
                  <SelectTrigger id="accountingCollectionMethod"><SelectValue placeholder="Selecciona el método" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="transfer">Transferencia</SelectItem>
                    <SelectItem value="confirming">Confirming</SelectItem>
                    <SelectItem value="promissory_note">Pagaré</SelectItem>
                    <SelectItem value="cash">Contado</SelectItem>
                    <SelectItem value="other">Otro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="grid gap-2">
                  <Label htmlFor="accountingConfirmingDate">Fecha de cobro *</Label>
                  <Input
                    id="accountingConfirmingDate"
                    type="date"
                    value={settlementDate}
                    onChange={(event) => setSettlementDate(event.target.value)}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="accountingConfirmingGross">Importe satisfecho bruto (€) *</Label>
                  <Input
                    id="accountingConfirmingGross"
                    type="number"
                    min="0"
                    step="0.01"
                    inputMode="decimal"
                    value={settlementGross}
                    onChange={(event) => setSettlementGross(event.target.value)}
                  />
                </div>
                <div className="grid gap-2 sm:col-span-2">
                  <Label htmlFor="accountingCollectionReference">Referencia bancaria (opcional)</Label>
                  <Input id="accountingCollectionReference" value={settlementReference} maxLength={255} onChange={(event) => setSettlementReference(event.target.value)} />
                </div>
                {settlementMethod === "confirming" ? (
                  <>
                    <div className="grid gap-2">
                      <Label htmlFor="accountingConfirmingCommission">Comisión bancaria (€)</Label>
                      <Input
                        id="accountingConfirmingCommission"
                        type="number"
                        min="0"
                        step="0.01"
                        inputMode="decimal"
                        value={settlementCommission}
                        onChange={(event) => setSettlementCommission(event.target.value)}
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="accountingConfirmingInterest">Intereses (€)</Label>
                      <Input
                        id="accountingConfirmingInterest"
                        type="number"
                        min="0"
                        step="0.01"
                        inputMode="decimal"
                        value={settlementInterest}
                        onChange={(event) => setSettlementInterest(event.target.value)}
                      />
                    </div>
                  </>
                ) : null}
              </div>
              {settlementMethod === "confirming" ? (
                <div className="grid gap-2 rounded-md border p-3 text-sm sm:grid-cols-2">
                  <div>
                    <span className="text-muted-foreground">Coste financiero:</span>{" "}
                    <span className="font-medium">
                      {confirmingFinancialCost() === null ? "—" : `${confirmingFinancialCost()!.toFixed(2)} €`}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Neto recibido banco:</span>{" "}
                    <span className="font-semibold">
                      {confirmingNetReceived() === null || confirmingNetReceived()! < 0
                        ? "—"
                        : `${confirmingNetReceived()!.toFixed(2)} €`}
                    </span>
                  </div>
                </div>
              ) : null}
              {settlementError ? (
                <p className="text-sm text-destructive" role="alert">{settlementError}</p>
              ) : null}
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={closeConfirmingSettlementDialog} disabled={recordCollectionMutation.isPending}>
              Cancelar
            </Button>
            <Button onClick={handleCollectionSubmit} disabled={recordCollectionMutation.isPending || !settlementDate.trim() || settlementMilestone?.invoiceFiscalVerified !== true || (() => {
              if (!settlementMilestone) return true;
              const gross = parseSettlementMoney(settlementGross);
              if (gross === null) return true;
              const balance = parseSettlementMoney(settlementMilestone.balanceAmount ?? "");
              return balance === null || balance <= 0 || Math.round(gross * 100) > Math.round(balance * 100);
            })()}>
              {recordCollectionMutation.isPending ? "Guardando..." : (parseSettlementMoney(settlementMilestone?.balanceAmount ?? "") ?? 0) - (parseSettlementMoney(settlementGross) ?? 0) > 0.005 ? "Registrar cobro parcial" : "Registrar cobro"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function getWorkOrderTypeLabel(workOrderType?: string) {
  const labels: { [key: string]: string } = {
    installation: "Instalación",
    breakdown: "Avería",
    maintenance: "Mantenimiento",
  };
  return labels[workOrderType || "installation"] || workOrderType || "Instalación";
}

function MobileField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid min-w-0 grid-cols-1 gap-1 border-b border-gray-100 pb-2 min-[420px]:grid-cols-[7.5rem_minmax(0,1fr)] min-[420px]:gap-3 last:border-b-0 last:pb-0">
      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</dt>
      <dd className="min-w-0 text-left text-gray-900 break-words [overflow-wrap:anywhere] min-[420px]:text-right">{children}</dd>
    </div>
  );
}

function DetailItem({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="min-w-0 rounded-md border border-gray-200 bg-white p-3">
      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</p>
      <div className="mt-1 break-words text-sm text-gray-900">
        {value || <span className="text-gray-400">-</span>}
      </div>
    </div>
  );
}

function getStatusBadge(
  milestone: Pick<BillingMilestone, "status" | "collectionStatus" | "balanceAmount" | "invoiceNumber" | "invoiceDate" | "invoiceFiscalVerified">,
  invoicePaidPendingReview = false,
) {
  const statusColors: { [key: string]: string } = {
    pending: "bg-gray-100 text-gray-800",
    ready_to_invoice: "bg-blue-100 text-blue-800",
    accounting_notified: "bg-purple-100 text-purple-800",
    invoiced: "bg-green-100 text-green-800",
    sent_to_client: "bg-cyan-100 text-cyan-800",
    pending_collection: "bg-orange-100 text-orange-800",
    invoice_review: "bg-amber-100 text-amber-900",
    collected: "bg-green-600 text-white",
    cancelled: "bg-red-100 text-red-800",
  };
  const labels: { [key: string]: string } = {
    pending: "Planificado / no facturable aún",
    ready_to_invoice: "Listo para facturar",
    accounting_notified: "Avisado a contabilidad",
    invoiced: "Factura emitida",
    sent_to_client: "Enviado al cliente",
    pending_collection: "Pendiente de cobro",
    invoice_review: INVOICE_REVIEW_REQUIRED_LABEL,
    collected: "Cobrado",
    cancelled: "Cancelado",
  };
  const status = billingMilestoneDisplayStatus(milestone);
  return {
    className: invoicePaidPendingReview ? "bg-amber-100 text-amber-900" : statusColors[status] || "bg-gray-100 text-gray-800",
    label: invoicePaidPendingReview ? INVOICE_PAID_PENDING_ECONOMIC_REVIEW_LABEL : labels[status] || status,
  };
}

function isInvoiceCollectionComplete(milestone: Pick<BillingMilestone, "status" | "collectionStatus" | "balanceAmount">) {
  return isLedgerInvoiceCollectionComplete(milestone);
}
